import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveApiError } from '../utils/i18n';
import { apiService } from '../services/api';
import { useAlert } from '../contexts/AlertContext';
import { executeRecaptcha } from '../utils/recaptcha';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound } from 'lucide-react';

import { COUNTRY_CODES } from '../constants/phoneCountryCodes';
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,15}$/;

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [countryCode, setCountryCode] = useState('+852');
  const [localPhone, setLocalPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendCount, setSendCount] = useState(0);

  const whatsappPhone = `${countryCode}${localPhone.trim()}`;

  const onSendCode = async () => {
    if (!localPhone.trim()) return;
    setLoading(true);
    try {
      // reCAPTCHA required after 2 prior sends (backend triggers at 3rd attempt)
      const captchaToken = sendCount >= 2 ? await executeRecaptcha('forgot_password') : undefined;
      await apiService.users.forgotPasswordRequestCode(whatsappPhone, captchaToken);
      setSendCount(c => c + 1);
      setCodeSent(true);
      showAlert({ type: 'success', message: t('forgotPassword.codeSent', '如該電話已註冊，驗證碼已發送至您的 WhatsApp，請於 10 分鐘內輸入。') });
    } catch (err: any) {
      const errorCode = err?.response?.data?.errorCode;
      if (errorCode === 'CAPTCHA_SCORE_LOW') {
        showAlert({ type: 'warning', message: t('auth.captchaScoreLow', '請完成圖形驗證後再試') });
      } else {
        showAlert({ type: 'error', message: resolveApiError(errorCode, err?.response?.data?.message, t) });
      }
    } finally {
      setLoading(false);
    }
  };

  const onReset = async () => {
    if (!PASSWORD_REGEX.test(newPassword)) {
      showAlert({ type: 'warning', message: t('auth.passwordComplexity', '密碼須為6-15位，並包含大楷、小楷、數字及特殊字符') });
      return;
    }
    setLoading(true);
    try {
      await apiService.users.forgotPasswordReset(whatsappPhone, code.trim(), newPassword);
      showAlert({ type: 'success', message: t('forgotPassword.resetSuccess', '密碼重設成功，請使用新密碼登入') });
      navigate('/login-form');
    } catch (err: any) {
      showAlert({ type: 'error', message: resolveApiError(err?.response?.data?.errorCode, err?.response?.data?.message, t) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <div className="flex overflow-hidden rounded-t-xl">
            <div className="w-[30%] flex items-center justify-center py-8">
              <KeyRound className="w-10 h-10 text-primary" />
            </div>
            <div className="w-[70%] flex flex-col justify-center px-6 py-6">
              <p className="text-xl font-bold mb-1 leading-none">{t('forgotPassword.title', '忘記密碼')}</p>
              <p className="text-sm text-muted-foreground">{t('forgotPassword.subtitle', '透過 WhatsApp 驗證碼重設密碼')}</p>
            </div>
          </div>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="localPhone">{t('login.phone', '電話號碼')}</Label>
              <div className="flex gap-2">
                <select
                  className="h-9 w-28 rounded-md border border-input bg-background px-2 text-sm"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  disabled={codeSent}
                >
                  {COUNTRY_CODES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input
                  id="localPhone"
                  type="tel"
                  placeholder="98765432"
                  className="flex-1"
                  value={localPhone}
                  onChange={(e) => setLocalPhone(e.target.value)}
                  disabled={codeSent}
                />
              </div>
            </div>

            {!codeSent ? (
              <Button className="w-full" disabled={loading || !localPhone.trim()} onClick={onSendCode}>
                {loading ? t('common.loading', '處理中...') : t('forgotPassword.sendCode', '發送驗證碼')}
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="code">{t('forgotPassword.code', '驗證碼')}</Label>
                  <Input id="code" value={code} onChange={(e) => setCode(e.target.value)}
                    placeholder={t('forgotPassword.codePlaceholder', '輸入驗證碼')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('forgotPassword.newPassword', '新密碼')}</Label>
                  <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('auth.passwordComplexity', '密碼須為6-15位，並包含大楷、小楷、數字及特殊字符')} />
                </div>
                <Button className="w-full" disabled={loading || !code.trim() || !newPassword} onClick={onReset}>
                  {loading ? t('common.loading', '處理中...') : t('forgotPassword.reset', '重設密碼')}
                </Button>
                <Button variant="link" className="w-full p-0 h-auto font-normal" onClick={onSendCode} disabled={loading}>
                  {t('forgotPassword.resend', '重新發送驗證碼')}
                </Button>
              </>
            )}

            <div className="text-center text-sm">
              <Button variant="link" className="p-0 h-auto font-normal" onClick={() => navigate('/login-form')}>
                {t('forgotPassword.backToLogin', '返回登入')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
